import type { ZodType } from "zod";
import {
  listContentSchema,
  listItemContentSchema,
  placeContentSchema,
  placeReviewContentSchema,
  spaceContentSchema,
  wishContentSchema,
  type ContentBlob,
  type List,
  type ListItem,
  type Place,
  type SpaceMember,
  type SpaceSummary,
  type Wish,
} from "@vmeste/shared";
import { t } from "../i18n/index.js";
import type { SpaceState } from "./store.js";
import { ratingOf, reviewsOf, selectItems, selectLists, selectPlaces, selectWishes } from "./store.js";
import type {
  ListItemView,
  ListView,
  MemberView,
  PlaceView,
  SpaceView,
  WishView,
} from "../app/views.js";
import { plaintextOf } from "./plaintext.js";

/**
 * Turning wire rows into what components render.
 *
 * Components never see a ContentBlob. That is the whole point of the codec
 * boundary (SPEC §3): switching a space to `e2ee` changes what happens here and
 * nothing below.
 */

/**
 * Decode a blob synchronously, whatever mode it is in.
 *
 * No codec here at all: `plaintextOf` answers from what the sync layer already
 * decrypted before it applied the delta, so this stays synchronous for an e2ee
 * space exactly as it is for a plaintext one. A blob with no answer yet — no
 * key on this device — comes back null, and every caller draws its placeholder.
 */
function decode<T>(blob: ContentBlob, schema: ZodType<T>): T | null {
  const value = plaintextOf(blob);
  if (value === undefined) return null;

  const parsed = schema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function decodeSpaceName(blob: ContentBlob): string {
  return decode(blob, spaceContentSchema)?.name ?? t("search.spaceFallback");
}

export function toSpaceView(summary: SpaceSummary): SpaceView {
  return {
    id: summary.id,
    name: decodeSpaceName(summary.content),
    role: summary.role,
    memberCount: summary.memberCount,
    encryptionMode: summary.encryptionMode,
  };
}

export function toMemberView(member: SpaceMember): MemberView {
  return { user: member.user, role: member.role, joinedAt: member.joinedAt };
}

const DEFAULT_ICON: Record<List["kind"], string> = {
  todo: "✓",
  shopping: "🛒",
};

function toItemView(item: ListItem): ListItemView {
  const content = decode(item.content, listItemContentSchema);

  return {
    id: item.id,
    // A blob this client cannot read still occupies a row, and showing a
    // placeholder is better than dropping somebody's item from the list.
    text: content?.text ?? "…",
    ...(content?.note ? { note: content.note } : {}),
    done: item.done,
    doneBy: item.doneBy,
    dueAt: item.dueAt,
    assigneeId: item.assigneeId,
  };
}

function toListView(state: SpaceState, list: List): ListView {
  const content = decode(list.content, listContentSchema);

  return {
    id: list.id,
    kind: list.kind,
    title: content?.title ?? "…",
    icon: content?.icon ?? DEFAULT_ICON[list.kind],
    archived: list.archivedAt !== null,
    items: selectItems(state, list.id).map(toItemView),
  };
}

/**
 * Every list in a space, archived ones included and marked as such.
 *
 * Included rather than filtered here, because the screen is what decides where
 * an archived list goes — and it has to be able to offer the way back out.
 */
export function toListViews(state: SpaceState): ListView[] {
  return selectLists(state, { includeArchived: true }).map((list) =>
    toListView(state, list),
  );
}

/**
 * The host of a wish's link, shown instead of the raw URL.
 *
 * A marketplace URL is a hundred characters of tracking parameters; "ozon.ru"
 * is the part a person actually reads.
 */
function hostOf(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

function toWishView(wish: Wish): WishView {
  const content = decode(wish.content, wishContentSchema);
  const title = content?.preview?.title ?? content?.title;

  return {
    id: wish.id,
    // A blob this client cannot read still occupies a row; a placeholder beats
    // dropping somebody's wish off the screen.
    title: title ?? "…",
    ...(content?.url ? { url: content.url } : {}),
    ...(hostOf(content?.url) ? { host: hostOf(content?.url)! } : {}),
    ...(content?.note ? { note: content.note } : {}),
    ...(content?.price ? { price: content.price } : {}),
    status: wish.status,
    claimedBy: wish.claimedBy,
    addedBy: wish.addedBy,
  };
}

/**
 * The pictures hung on one place, ours-origin paths, oldest first.
 *
 * Read straight out of the synced attachments rather than through
 * `useAttachments`: the map draws every pin at once and needs one thumbnail
 * each, and a hook per pin is a hook count that changes as places arrive.
 */
function photosOf(state: SpaceState, placeId: string): string[] {
  return Object.values(state.attachments)
    .filter(
      (file) =>
        file.targetType === "place" && file.targetId === placeId && file.deletedAt === null,
    )
    .sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0))
    .map((file) => file.url);
}

function toPlaceView(state: SpaceState, place: Place): PlaceView {
  const content = decode(place.content, placeContentSchema);
  const photos = photosOf(state, place.id);

  const reviews = reviewsOf(state, place.id)
    .map((review) => {
      const words = review.content
        ? decode(review.content, placeReviewContentSchema)?.text
        : undefined;

      return {
        userId: review.userId,
        rating: review.rating,
        ...(words ? { text: words } : {}),
        updatedAt: review.updatedAt,
      };
    })
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));

  return {
    id: place.id,
    // A blob this device cannot read still occupies a row: a placeholder beats
    // dropping somebody's place off the screen.
    title: content?.title ?? "…",
    ...(content?.address ? { address: content.address } : {}),
    ...(content?.note ? { note: content.note } : {}),
    ...(content?.url ? { url: content.url } : {}),
    point: content ? { lat: content.lat, lng: content.lng } : null,
    visited: place.visitedAt !== null,
    visitedAt: place.visitedAt,
    rating: ratingOf(state, place.id),
    reviews,
    ...(photos[0] ? { photo: photos[0] } : {}),
    photoCount: photos.length,
    createdBy: place.createdBy,
    createdAt: place.createdAt,
  };
}

export function toPlaceViews(state: SpaceState): PlaceView[] {
  return selectPlaces(state).map((place) => toPlaceView(state, place));
}

export function toWishViews(state: SpaceState): WishView[] {
  return selectWishes(state).map(toWishView);
}
